import React, { useMemo, useState } from "react";
import { ArrowDown, ArrowUp, ChevronLeft, ChevronRight } from "lucide-react";

// Tabela ordenável com paginação. colunas: { id, rotulo, render(linha), valor(linha), alinhar: "direita", classe }
export default function Tabela({ colunas, linhas, ordemInicial, porPagina = 15, onClicar, vazio = "Nada no período.", e2e, chave = (l, i) => l.id || i }) {
  const [ordem, setOrdem] = useState(ordemInicial || null);
  const [pagina, setPagina] = useState(0);
  const ordenadas = useMemo(() => {
    if (!ordem) return linhas || [];
    const col = colunas.find((c) => c.id === ordem.id);
    if (!col?.valor) return linhas || [];
    const v = (l) => { const x = col.valor(l); return x == null ? -Infinity : x; };
    return [...(linhas || [])].sort((a, b) => {
      const x = v(a), y = v(b);
      const cmp = typeof x === "string" ? String(x).localeCompare(String(y)) : x - y;
      return ordem.desc ? -cmp : cmp;
    });
  }, [linhas, ordem, colunas]);
  const paginas = Math.max(1, Math.ceil(ordenadas.length / porPagina));
  const atual = Math.min(pagina, paginas - 1);
  const visiveis = ordenadas.slice(atual * porPagina, (atual + 1) * porPagina);
  const ordenar = (c) => c.valor && setOrdem((o) => ({ id: c.id, desc: o?.id === c.id ? !o.desc : true }));
  return (
    <div data-e2e={e2e}>
      <div className="overflow-x-auto -mx-4 sm:mx-0">
        <table className="w-full text-sm min-w-[640px]">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wide text-muted-foreground border-b">
              {colunas.map((c) => (
                <th key={c.id} className={`py-2 px-3 font-semibold whitespace-nowrap ${c.alinhar === "direita" ? "text-right" : ""} ${c.valor ? "cursor-pointer select-none hover:text-foreground" : ""}`}
                  onClick={() => ordenar(c)}>
                  <span className="inline-flex items-center gap-1">
                    {c.rotulo}
                    {ordem?.id === c.id && (ordem.desc ? <ArrowDown className="w-3 h-3" /> : <ArrowUp className="w-3 h-3" />)}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="tabular-nums">
            {visiveis.length === 0 && (
              <tr><td colSpan={colunas.length} className="py-8 text-center text-muted-foreground">{vazio}</td></tr>
            )}
            {visiveis.map((l, i) => (
              <tr key={chave(l, i)} onClick={onClicar ? () => onClicar(l) : undefined}
                className={`border-b last:border-0 ${onClicar ? "cursor-pointer hover:bg-muted/50" : ""}`}>
                {colunas.map((c) => (
                  <td key={c.id} className={`py-2.5 px-3 ${c.alinhar === "direita" ? "text-right whitespace-nowrap" : ""} ${c.classe || ""}`}>{c.render(l)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {paginas > 1 && (
        <div className="flex items-center justify-between pt-3 text-xs text-muted-foreground">
          <span>{ordenadas.length} linhas</span>
          <div className="flex items-center gap-2">
            <button type="button" disabled={atual === 0} onClick={() => setPagina(atual - 1)} className="p-1 rounded border disabled:opacity-30" aria-label="Página anterior"><ChevronLeft className="w-4 h-4" /></button>
            <span className="tabular-nums">{atual + 1} / {paginas}</span>
            <button type="button" disabled={atual >= paginas - 1} onClick={() => setPagina(atual + 1)} className="p-1 rounded border disabled:opacity-30" aria-label="Próxima página"><ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>
      )}
    </div>
  );
}
